import type { CreateAdminTypePropertyDto, UpdateAdminTypePropertyDto } from '@idc-forms/api-client'
import type {
  V2CreateAdminTypePropertyInput,
  V2UpdateAdminTypePropertyInput
} from '@idc-forms/schemas/api-v2/admin-type-properties'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { typePropertiesAdminV2Service } from '../services/type-properties-admin-v2.service'

/**
 * Mutaciones de subtipos contra la API v2. Identifican por id público (`typ_`) y,
 * al completar, invalidan todo el árbol `['type-properties']` (listado + summary +
 * detalle). El alta/edición vive en el side panel.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateTypePropertyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado y el schema describen el mismo dato en runtime pero TS los ve
    // como tipos distintos.
    mutationFn: async (body: V2CreateAdminTypePropertyInput) =>
      await typePropertiesAdminV2Service.create(accessToken, body as CreateAdminTypePropertyDto),
    onSuccess: () => {
      toast.success('Subtipo creado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.typeProperties.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdateTypePropertyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminTypePropertyInput }) =>
      await typePropertiesAdminV2Service.update(
        accessToken,
        id,
        body as UpdateAdminTypePropertyDto
      ),
    onSuccess: () => {
      toast.success('Subtipo actualizado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.typeProperties.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useDeleteTypePropertyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await typePropertiesAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Subtipo eliminado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.typeProperties.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
